import { Fragment, useMemo } from 'react';
import type {
  ArticleHeader,
  ArticleModule,
  ArticleV2 as ArticleV2PageType
} from 'types/capi/article';

import BrandedHeading from '~/components/Content/BrandedHeading';
import BrandedQuote from '~/components/Content/BrandedQuote';
import ModuleImpression from '~/components/ModuleImpression';
import usePageData from '~/hooks/usePageData';
import useStructuredData from '~/hooks/useStructuredData';
import ArticleCarousel from '~/modules/ArticleCarousel';
import RecentFeatures from '~/modules/RecentFeatures';
import ContentBlock from '~/modules/ste/ContentBlock';
import EmbedBlock from '~/modules/ste/EmbedBlock';
import ImageGrid from '~/modules/ste/ImageGrid';
import ModuleRow from '~/modules/ste/ModuleRow';
import ProductList from '~/modules/ste/ProductList';
import { getModuleImpressionProps } from '~/utils/moduleImpression';
import {
  getModulePositions,
  type ModulePositions
} from '~/utils/modulePositions';

import CommissionDisclaimer from './CommissionDisclaimer';
import { Footer, Header } from './HeaderFooter';
import HeaderImage from './HeaderImage';

function getHeaderVisibilityClass(header?: ArticleHeader): string | undefined {
  if (!header) return undefined;

  const desktopOverlay = header.desktop.template === 'title_over_image';
  const mobileOverlay = header.mobile.template === 'title_over_image';

  if (desktopOverlay && mobileOverlay) return 'hidden';
  if (desktopOverlay) return 'md:hidden';
  if (mobileOverlay) return 'hidden md:flex';

  return undefined;
}

export default function ArticleV2() {
  const {
    id,
    title,
    core_images,
    display_header,
    header,
    author,
    dates,
    article_type,
    _schemas
  } = usePageData<ArticleV2PageType>();

  useStructuredData(_schemas);

  const showHeaderImage = header ? display_header !== false : display_header;
  const headerVisibilityClass = showHeaderImage
    ? getHeaderVisibilityClass(header)
    : undefined;

  return (
    <>
      <CommissionDisclaimer />
      <div
        key={id}
        className="flex w-full flex-col gap-[64px] bg-grey-tile max-md:gap-6"
      >
        <ModuleImpression moduleType="article-header">
          {showHeaderImage && (
            <HeaderImage
              header={header}
              images={core_images}
              articleType={article_type}
              title={title}
              author={author}
              publishedDate={dates.published}
              modifiedDate={dates.modified}
            />
          )}
          <Header
            author={author}
            title={title}
            publishedDate={dates.published}
            modifiedDate={dates.modified}
            className={headerVisibilityClass}
          />
        </ModuleImpression>
        <Modules />
        {article_type === 'news' ? <NewsFooterContent /> : <FooterContent />}
      </div>
    </>
  );
}

function renderModule(module: ArticleModule, modulePositions: ModulePositions) {
  const modulePosition = modulePositions.get(module);

  switch (module.type) {
    case 'branded-quote':
      return <BrandedQuote quote={module} />;
    case 'branded-heading':
      return <BrandedHeading module={module} />;
    case 'content-block':
      if (!module.content?.length) return null;

      return <ContentBlock {...module} />;
    case 'embed':
      return <EmbedBlock embed={module} modulePosition={modulePosition} />;
    case 'image-grid':
      return <ImageGrid module={module} modulePosition={modulePosition} />;
    case 'module-row':
      return <ModuleRow module={module} modulePositions={modulePositions} />;
    case 'product-list':
      return (
        <ProductList
          {...module}
          modulePosition={modulePosition}
          pageType="article"
        />
      );
    default:
      return null;
  }
}

function Modules() {
  const { modules } = usePageData<ArticleV2PageType>();
  const modulePositions = useMemo(() => getModulePositions(modules), [modules]);

  return (
    <>
      {modules.map((module, index) => {
        const rendered = renderModule(module, modulePositions);

        if (!rendered || module.type === 'module-row') {
          return <Fragment key={index}>{rendered}</Fragment>;
        }

        return (
          <ModuleImpression
            key={index}
            modulePosition={modulePositions.get(module)}
            moduleType={module.type}
            {...getModuleImpressionProps(module)}
          >
            {rendered}
          </ModuleImpression>
        );
      })}
    </>
  );
}

function RecentFeaturesModule({
  items
}: {
  items: ArticleV2PageType['recent_features'];
}) {
  if (!items?.length) return null;

  return (
    <ModuleImpression itemCount={items.length} moduleType="recent-features">
      <RecentFeatures title="Discover more features" items={items} />
    </ModuleImpression>
  );
}

function LatestNewsModule({
  items
}: {
  items: ArticleV2PageType['latest_news'];
}) {
  if (!items?.length) return null;

  return (
    <ModuleImpression itemCount={items.length} moduleType="article-latest-news">
      <ArticleCarousel items={items} title="Related articles" />
    </ModuleImpression>
  );
}

function FooterContent() {
  const { author, title, dates, recent_features, latest_news } =
    usePageData<ArticleV2PageType>();

  return (
    <div>
      <div className="w-full bg-black">
        <Footer
          author={author}
          title={title}
          publishedDate={dates.published}
          modifiedDate={dates.modified}
        />
        <div className="mx-auto h-px w-8/12 max-w-[600px] bg-white" />
        <RecentFeaturesModule items={recent_features} />
      </div>
      <LatestNewsModule items={latest_news} />
    </div>
  );
}

function NewsFooterContent() {
  const { author, title, dates, recent_features, latest_news } =
    usePageData<ArticleV2PageType>();

  return (
    <div>
      <Footer
        author={author}
        title={title}
        publishedDate={dates.published}
        modifiedDate={dates.modified}
        variant="light"
      />
      <LatestNewsModule items={latest_news} />
      <div className="w-full bg-black">
        <RecentFeaturesModule items={recent_features} />
      </div>
    </div>
  );
}
